/**
 * useStudioWorkspace – Phase 4 Workspace Studio.
 *
 * Manages workspace load, draft version, and publish lifecycle for the Studio.
 *
 * Resolution:
 *   1. Load workspace by ID
 *   2. Load current draft version (if currentDraftVersionId exists)
 *   3. If no draft, fall through to published version as editing baseline
 *      (first mutation will auto-create a draft via copy-on-write)
 *
 * Publish:
 *   - Calls POST /workspaces/{id}/publish
 *   - Reloads workspace state after publish
 *
 * NEVER modifies published versions.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getWorkspace,
  getDraftVersion,
  getPublishedVersion,
  publishWorkspace,
  updateWorkspaceDraft,
  assignWorkspace,
} from '../../api/workspaceApi';
import { flushDraftSave, subscribeSaveStatus } from '../../services/workspaceRuntime/workspaceDraftService';
import {
  workspaceRuntimeContext,
  legacyRuntimeContext,
  RUNTIME_MODE,
} from '../../services/workspaceRuntime/workspaceResolverService';
import {
  normalizeRuntimeLayout,
  RUNTIME_SOURCE,
} from '../../services/workspaceRuntime/workspaceLayoutAdapter';
import { normalizeIcons } from '../../services/homeLauncher/launcherLayoutService';
import { DEFAULT_ICONS } from '../../services/homeLauncher/launcherPersistenceService';

/**
 * @param {string|null} workspaceId
 */
export default function useStudioWorkspace(workspaceId) {
  const [workspace,     setWorkspace]     = useState(null);
  const [draftVersion,  setDraftVersion]  = useState(null);
  const [isLoading,     setIsLoading]     = useState(false);
  const [error,         setError]         = useState(null);
  const [isPublishing,  setIsPublishing]  = useState(false);
  const [publishError,  setPublishError]  = useState(null);
  const [publishedAt,   setPublishedAt]   = useState(null);

  const loadWorkspace = useCallback(async (id) => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    setWorkspace(null);
    setDraftVersion(null);

    try {
      const wsResp = await getWorkspace(id);
      const ws     = wsResp?.data ?? wsResp;
      if (!ws) throw new Error('Workspace not found');

      let version = null;

      if (ws.currentDraftVersionId) {
        try {
          const draftResp = await getDraftVersion(id);
          version = draftResp?.data ?? draftResp;
        } catch (_) {
          // draft load failed – fall through to published
        }
      }

      if (!version && ws.publishedVersionId) {
        try {
          const pubResp = await getPublishedVersion(id);
          version = pubResp?.data ?? pubResp;
        } catch (_) {}
      }

      setWorkspace(ws);
      setDraftVersion(version);
    } catch (err) {
      setError(err?.message || 'Failed to load workspace');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWorkspace(workspaceId);
  }, [workspaceId, loadWorkspace]);

  // When the debounce auto-creates a draft, re-fetch workspace so
  // currentDraftVersionId is reflected in state (keeps hasDraftVersion accurate).
  const workspaceRef     = useRef(null);
  const draftVersionRef  = useRef(null);
  workspaceRef.current    = workspace;
  draftVersionRef.current = draftVersion;
  useEffect(() => {
    if (!workspaceId) return;
    const unsub = subscribeSaveStatus((status) => {
      if (status === 'saved' && !workspaceRef.current?.currentDraftVersionId) {
        getWorkspace(workspaceId).then((resp) => {
          const ws = resp?.data ?? resp;
          if (ws) setWorkspace(ws);
        }).catch(() => {});
      }
    });
    return unsub;
  }, [workspaceId]);

  const publish = useCallback(async (layoutSnapshot = null) => {
    if (!workspaceId) return false;
    setIsPublishing(true);
    setPublishError(null);
    try {
      const ctx = workspaceRuntimeContext(
        workspaceRef.current || { id: workspaceId },
        draftVersionRef.current?.id || null,
        RUNTIME_MODE.DRAFT_EDIT,
        true,
      );
      if (layoutSnapshot) {
        await flushDraftSave(ctx, layoutSnapshot, { strict: true });
      } else {
        await flushDraftSave();
      }
      // Re-fetch workspace to get the latest draft state (the flush above may have
      // just created a draft, or a prior debounce save already did so)
      const preCheckResp = await getWorkspace(workspaceId);
      const preCheckWs   = preCheckResp?.data ?? preCheckResp;
      if (!preCheckWs?.currentDraftVersionId) {
        // No draft exists – auto-create one from the current loaded layout
        // (copy-on-write from published version via the PATCH endpoint)
        const schema = layoutSnapshot
          ? {
              icons: Array.isArray(layoutSnapshot.icons) ? layoutSnapshot.icons : [],
              theme: layoutSnapshot.theme || 'system',
              gridColumns: layoutSnapshot.gridColumns || 4,
              gridRows: layoutSnapshot.gridRows || 6,
              iconSize: layoutSnapshot.iconSize || 'medium',
              gap: layoutSnapshot.gap ?? 2,
              showGrid: layoutSnapshot.showGrid || false,
              backgroundImageUrl: layoutSnapshot.backgroundImageUrl || '',
            }
          : draftVersionRef.current?.layoutSchema ?? {};
        await updateWorkspaceDraft(workspaceId, schema);
      }
      await publishWorkspace(workspaceId);
      try {
        await assignWorkspace(workspaceId, { targetType: 'tenantDefault', targetId: null });
      } catch (assignErr) {
        console.warn('[WorkspaceStudio] Published, but tenant default assignment failed', assignErr);
      }
      const wsResp = await getWorkspace(workspaceId);
      const ws     = wsResp?.data ?? wsResp;
      setWorkspace(ws);
      // After publish the draft is archived – load the new published version as baseline
      if (ws?.publishedVersionId) {
        const pubResp = await getPublishedVersion(workspaceId);
        setDraftVersion(pubResp?.data ?? pubResp);
      } else {
        setDraftVersion(null);
      }
      setPublishedAt(new Date().toISOString());
      return true;
    } catch (err) {
      setPublishError(err?.message || 'Publish failed');
      return false;
    } finally {
      setIsPublishing(false);
    }
  }, [workspaceId]);

  const runtimeContext = workspaceId && workspace
    ? workspaceRuntimeContext(workspace, draftVersion?.id || null, RUNTIME_MODE.DRAFT_EDIT)
    : legacyRuntimeContext();

  const rawLayoutData = draftVersion
    ? normalizeRuntimeLayout(RUNTIME_SOURCE.WORKSPACE, draftVersion.layoutSchema || {})
    : null;

  const initialIcons = rawLayoutData
    ? normalizeIcons(rawLayoutData.icons, DEFAULT_ICONS)
    : [];

  return {
    workspace,
    draftVersion,
    runtimeContext,
    rawLayoutData,
    initialIcons,
    isLoading,
    error,
    publish,
    isPublishing,
    publishError,
    publishedAt,
    reload: () => loadWorkspace(workspaceId),
  };
}
